import { useMemo } from 'react';

import { EmailComposer, EmailComposerConfig } from 'components/EmailComposer';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import useFetchEmailTemplates from 'hooks/useFetchEmailTemplates';
import useFetchSuggestedSchedulingRecipients from 'hooks/useFetchSuggestedSchedulingRecipients';
import useSendAsAddresses from 'hooks/useSendAsAddresses';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { copyRequestApptEmail } from 'lib/api/scheduling/copyRequestApptEmail';
import { sendRequestApptEmail } from 'lib/api/scheduling/sendRequestApptEmail';
import { StopTypes } from 'types/Appointment';
import { EmailTemplate, TemplateType } from 'types/EmailTemplates';
import { NormalizedLoad } from 'types/Load';
import { nl2br } from 'utils/getJoditEditorHelpers';

import EmailSchedulingHeader from './EmailSchedulingHeader';
import { buildSchedulingRecipients } from './helpers/buildSchedulingRecipients';

interface EmailSchedulingProps {
  stopType: StopTypes;
  load: NormalizedLoad;
  onBack: () => void;
}

const EmailScheduling = ({ stopType, load, onBack }: EmailSchedulingProps) => {
  const { addresses: sendAsAddresses } = useSendAsAddresses();
  const { isSharedMailboxUser } = useServiceFeatures();
  const { emailTemplates } = useFetchEmailTemplates(
    load.ID,
    TemplateType.APPOINTMENT,
    stopType
  );
  const { emails: learnedEmails, isLoading: isLoadingSuggested } =
    useFetchSuggestedSchedulingRecipients(load.ID, stopType);

  const tmsEmail =
    stopType === StopTypes.Pickup
      ? load?.pickup?.email || ''
      : load?.consignee?.email || '';

  const tmsContactName =
    stopType === StopTypes.Pickup
      ? load?.pickup?.name || 'Pickup Contact'
      : load?.consignee?.name || 'Consignee Contact';

  const { initialRecipients, suggestedRecipients } = useMemo(
    () =>
      buildSchedulingRecipients({
        tmsEmail,
        tmsContactName,
        learnedEmails,
      }),
    [tmsEmail, tmsContactName, learnedEmails]
  );

  const sendEmail = async (params: any) => {
    return sendRequestApptEmail({
      loadID: params.loadID,
      recipients: params.recipients,
      cc: params.cc,
      bcc: params.bcc,
      subject: params.subject,
      emailBody: params.emailBody,
      emailTemplateId: params.emailTemplateId,
      stopType: stopType,
      replyInThread: params.replyInThread,
      threadID: params.threadID,
      threadItemID: params.threadItemID,
      threadReferences: params.threadReferences,
      inReplyTo: params.inReplyTo,
      from: params.from,
    });
  };

  const copyEmail = async (params: any) => {
    if (!emailTemplates?.appointmentRequestTemplateHTML) {
      return { success: false, error: 'No template available to copy.' };
    }

    await copyRequestApptEmail(
      params.emailBody,
      emailTemplates,
      params.freightTrackingId || load.freightTrackingID,
      stopType
    );

    return { success: true };
  };

  const getDefaultSubject = (template?: EmailTemplate): string => {
    return (
      template?.subject ||
      emailTemplates?.appointmentRequestTemplateHTML?.subject ||
      ''
    );
  };

  const getDefaultEmailBody = (template?: EmailTemplate): string => {
    return nl2br(template?.body || '');
  };

  const config: EmailComposerConfig = {
    templateType: TemplateType.APPOINTMENT,
    loadID: load.ID!,
    freightTrackingId: load.freightTrackingID,
    stopType,
    defaultTemplateId: emailTemplates?.appointmentRequestTemplateHTML?.id,
    extractTemplates: (emailTemplates: any) => {
      return emailTemplates?.apptRequestTemplates || [];
    },
    initialRecipients,
    requireSubject: true,
    showSubjectForNewEmailOnly: true,
    sendEmail,
    copyEmail,
    successMessage: 'Appointment request email sent successfully!',
    replySuccessMessage: 'Appointment request reply sent successfully!',
    errorMessage: 'Failed to send appointment request email',
    showAIFilled: !!emailTemplates?.appointmentRequestTemplateHTML?.body,
    sendAsAddresses,
    isLoggedInUserSharedMailbox: isSharedMailboxUser ?? false,
  };

  const headerComponent = (
    <EmailSchedulingHeader
      onScheduleAnotherWay={onBack}
      showAIFilled={!!emailTemplates?.appointmentRequestTemplateHTML?.body}
      stopType={stopType}
    />
  );

  // When TMS has no email, wait for learned suggestions so To can prefill on mount.
  // When TMS has an email, render immediately; suggestions hydrate into chips.
  const shouldWaitForSuggestions = !tmsEmail.trim() && isLoadingSuggested;
  if (shouldWaitForSuggestions) {
    return (
      <>
        {headerComponent}
        <Flex align='center' gap='sm' className='mt-4'>
          <ButtonLoader className='mr-0' />
          <Typography variant='body-sm' className='text-neutral-600'>
            Loading suggested recipients...
          </Typography>
        </Flex>
      </>
    );
  }

  return (
    <EmailComposer
      config={config}
      headerComponent={headerComponent}
      initialRecipients={initialRecipients}
      aiSuggestedTo={suggestedRecipients}
      getDefaultSubject={getDefaultSubject}
      getDefaultEmailBody={getDefaultEmailBody}
    />
  );
};

export default EmailScheduling;
